import AlertInline from '@storeblocks/inline-alert';
import { useField } from 'formik';
import { type FC } from 'react';
import { styled } from 'styled-components';

import { OrderValueType } from '@/api/order/models/OrderValueType';
import { ConnectSurface } from '@/components/Pretzel/ConnectSurface';
import { RenderHtml } from '@/components/RenderHtml';
import { useTexts } from '@/hooks/useTexts';
import { fmsWithTemplate } from '@/i18n/fmsWithTemplate';
import { useUserLocale } from '@/i18n/hooks';
import { SubscriptionPayment } from '@/pages/Order/steps/OrderDetails/types/Payment';
import { SubscriptionFundState } from '@/pages/Order/steps/OrderDetails/types/SubscriptionFundState';
import { SubscriptionOrderFormValues } from '@/pages/Order/steps/OrderDetails/types/SubscriptionOrderFormValues';
import { getPaymentTitleFromAccountHolder } from '@/pages/Order/utils/getPaymentTitleFromAccountHolder';
import { getSubscriptionPaymentTitle } from '@/pages/Order/utils/getSubscriptionPaymentTitle';
import { formatNumber } from '@/util/formatNumber';
import { nameof } from '@/util/nameof';

interface Props {
  payment: SubscriptionPayment;
}

export const SubscriptionPaymentInformation: FC<Props> = ({ payment }) => {
  const texts = useTexts();
  const locale = useUserLocale();

  const [{ value: funds }] = useField<SubscriptionFundState[]>(
    nameof<SubscriptionOrderFormValues>('toFunds'),
  );

  const totalAmount = funds
    .filter((fund) => payment.forIsins.includes(fund.isin))
    .filter((fund) => fund.valueType === OrderValueType.Amount)
    .reduce((sum, fund) => sum + fund.value, 0);

  const formattedAmount = formatNumber(totalAmount, locale);

  const part1 = fmsWithTemplate(
    texts.orders.form.payment.subscription.settlementAccount.info.description
      .part1,
    {
      amount: formattedAmount,
      currency: payment.fundPaymentInformation.currency,
      accountHolder: getPaymentTitleFromAccountHolder(
        payment.fundPaymentInformation.accountHolder,
        texts,
      ),
    },
  );

  const part2 =
    texts.orders.form.payment.subscription.settlementAccount.info.description
      .part2;

  const clearing = payment.fundPaymentInformation.clearing
    ? fmsWithTemplate(
        texts.orders.form.payment.subscription.settlementAccount.info.clearing,
        {
          clearing: payment.fundPaymentInformation.clearing,
        },
      )
    : null;

  const bankAccountNumber = fmsWithTemplate(
    texts.orders.form.payment.subscription.settlementAccount.info
      .bankAccountNumber,
    {
      bankAccountNumber: payment.fundPaymentInformation.bankAccountNumber,
    },
  );

  const iban = fmsWithTemplate(
    texts.orders.form.payment.subscription.settlementAccount.info.iban,
    {
      iban: payment.fundPaymentInformation.iban,
    },
  );

  const swift = fmsWithTemplate(
    texts.orders.form.payment.subscription.settlementAccount.info.swift,
    {
      swift: payment.fundPaymentInformation.swift,
    },
  );

  const alertConfig =
    texts.orders.form.payment.subscription.settlementAccount.info.alert;

  // The actual value in FMS is a JSON object, but our typings in Connect
  // only handles values from FMS as strings. So we need to cast it to
  // unknown and then to the correct type.
  // Note: This alert is only shown for SEK payments.
  const isSek = payment.fundPaymentInformation.currency === 'SEK';
  const showAlert =
    isSek && (alertConfig.enabled as unknown as { enabled: boolean }).enabled;

  return (
    <StyledSurface>
      <Container>
        <strong>
          {getSubscriptionPaymentTitle(
            payment.fundPaymentInformation.accountHolder,
            payment.fundPaymentInformation.currency,
            texts,
            {
              displayCurrencyInTitle: false,
            },
          )}
        </strong>

        <div>
          <RenderHtml html={part1} />
          <div>{part2}</div>
        </div>

        {showAlert && (
          <StyledAlertInline variant="info" title={alertConfig.text} />
        )}

        <div>
          {clearing && <div>{clearing}</div>}
          <div>{bankAccountNumber}</div>
          <div>{iban}</div>
          <div>{swift}</div>
        </div>
      </Container>
    </StyledSurface>
  );
};

const StyledSurface = styled(ConnectSurface)`
  width: fit-content;

  /* Note that the strong element can also come from FMS as markdown. */
  & strong {
    font-weight: 600;
  }
`;

const StyledAlertInline = styled(AlertInline)`
  max-width: 500px;
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;
